// ABOUTME: Close At shortcut modal popup. Provides a quick way to close an order
// by setting quantityChangeExpected to false (no more plus loads wanted).

import {Button, Icon, Modal, ModalContent, ModalFooter, ModalTitle} from '@hm/fundamental'
import {mdiClose} from '@mdi/js'
import {Box, CircularProgress, Typography} from '@mui/material'
import React, {useCallback, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'

import ReviewIllustration from '../../../../../Assets/orderChangeResult/review.svg'
import SuccessIllustration from '../../../../../Assets/orderChangeResult/success.svg'
import {useSubmitOrderChange} from '../../../../../common/react-query/hooks/mutations/useSubmitOrderChange'
import {Order} from '../../../../../Pages/Order/Order.types'
import {useChangeOrderPolling} from '../../hooks/useChangeOrderPolling'
import {TransactionResultType} from '../../types/orderChangeResult.types'

type CloseAtStep = 'confirm' | 'result'

interface CloseAtModalProps {
  isOpen: boolean
  onClose: () => void
  order: Order
}

const closeButtonSx = {
  cursor: 'pointer',
  padding: '8px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {backgroundColor: 'rgba(0, 0, 0, 0.04)'}
}

export const CloseAtModal: React.FC<CloseAtModalProps> = ({isOpen, onClose, order}) => {
  const {t} = useTranslation()

  const [step, setStep] = useState<CloseAtStep>('confirm')
  const [requestId, setRequestId] = useState<string | undefined>(undefined)
  const [resultType, setResultType] = useState<TransactionResultType | undefined>(undefined)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('confirm')
      setRequestId(undefined)
      setResultType(undefined)
      setIsSubmitting(false)
    }
  }, [isOpen])

  const {mutate: submitChange} = useSubmitOrderChange({
    onSuccess: (data) => {
      setRequestId(data.requestId)
    },
    onError: () => {
      setResultType('failure')
      setStep('result')
      setIsSubmitting(false)
    }
  })

  const {
    triggerPolling,
    isPolling,
    result: pollingResult
  } = useChangeOrderPolling(order.orderId, requestId)

  useEffect(() => {
    if (requestId) {
      triggerPolling()
    }
  }, [requestId, triggerPolling])

  useEffect(() => {
    if (pollingResult) {
      setResultType(pollingResult)
      setStep('result')
      setIsSubmitting(false)
    }
  }, [pollingResult])

  const handleSubmit = useCallback(() => {
    setIsSubmitting(true)
    submitChange({
      orderId: order.orderId,
      payload: {checkSum: order.checkSum ?? '', quantityChangeExpected: false}
    })
  }, [order, submitChange])

  const isDismissible = !isSubmitting && !isPolling

  if (!isOpen) return null

  const orderQuantity = order.schedules?.[0]?.scheduleQuantity
  const quantityUom = order.lineItems?.[0]?.quantityUom ?? 'M3'

  // Result step — loading (still polling)
  if (step === 'result' && !resultType) {
    return (
      <Modal open onClose={() => {}} data-test-id="close-at-modal-loading">
        <ModalContent>
          <Box sx={{display: 'flex', justifyContent: 'center', padding: '48px'}}>
            <CircularProgress size={40} />
          </Box>
        </ModalContent>
      </Modal>
    )
  }

  // Result step — matches PinValidationModal success state structure
  if (step === 'result' && resultType) {
    const isSuccess = resultType === 'success'
    const resultTitle = isSuccess
      ? t('orderIntake.rmc.orderManagement.result.headerApproved', 'Order change approved')
      : resultType === 'failure'
        ? t('orderIntake.rmc.orderManagement.result.failureTitle', 'Order change failed')
        : t('orderIntake.rmc.orderManagement.result.headerSubmitted', 'Order change submitted')
    const resultDescription = isSuccess
      ? t(
          'orderIntake.rmc.orderManagement.result.descriptionApproved',
          'Your order will be updated in just a few seconds.'
        )
      : resultType === 'failure'
        ? t(
            'orderIntake.rmc.orderManagement.result.failureDescription',
            'Something went wrong. Please try again later.'
          )
        : t(
            'orderIntake.rmc.orderManagement.result.descriptionSubmitted',
            'Please check back later to see the status of your request.'
          )
    const illustration = isSuccess ? SuccessIllustration : ReviewIllustration

    return (
      <Modal open onClose={onClose} data-test-id="close-at-modal-result">
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <ModalTitle>{resultTitle}</ModalTitle>
          <Box onClick={onClose} data-test-id="close-at-modal-result-close" sx={closeButtonSx}>
            <Icon path={mdiClose} />
          </Box>
        </Box>
        <ModalContent>
          <Box sx={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '20px',
                color: '#54626F',
                textAlign: 'start'
              }}
            >
              {resultDescription}
            </Typography>
            <Box sx={{padding: '24px 0', display: 'flex', justifyContent: 'center'}}>
              <img src={illustration} alt={resultType} style={{width: '240px', height: 'auto'}} />
            </Box>
          </Box>
        </ModalContent>
        <ModalFooter>
          <Button
            variant="secondary"
            onClick={onClose}
            className="w-full"
            data-test-id="close-at-modal-close-result-button"
          >
            {t('orderIntake.rmc.orderManagement.result.close', 'Close')}
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  // Confirm step
  return (
    <Modal open onClose={isDismissible ? onClose : () => {}} data-test-id="close-at-modal">
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <ModalTitle>
          {t(
            'orderIntake.rmc.orderManagement.closeAt.title',
            'Close this order at {{quantity}} {{unit}}?',
            {
              quantity: orderQuantity,
              unit: quantityUom
            }
          )}
        </ModalTitle>
        {isDismissible && (
          <Box onClick={onClose} data-test-id="close-at-modal-close" sx={closeButtonSx}>
            <Icon path={mdiClose} />
          </Box>
        )}
      </Box>
      <ModalContent>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '20px',
              color: '#54626F'
            }}
          >
            {t(
              'orderIntake.rmc.orderManagement.closeAt.description',
              "Once this order is closed, you won't be able to request a plus load. If you are certain you have the required quantity, you can close the order now."
            )}
          </Typography>
        </Box>
      </ModalContent>
      <ModalFooter>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '12px', width: '100%'}}>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full"
            data-test-id="close-at-modal-confirm-button"
          >
            {isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t(
                'orderIntake.rmc.orderManagement.closeAt.confirm',
                'Close Order at {{quantity}} {{unit}}',
                {
                  quantity: orderQuantity,
                  unit: quantityUom
                }
              )
            )}
          </Button>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full"
            data-test-id="close-at-modal-cancel-button"
          >
            {t('orderIntake.rmc.orderManagement.closeAt.cancel', 'Cancel')}
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  )
}
